// src/components/leitner/LeitnerManager.jsx
// COMPLETE UPDATED FILE - Replace entire existing file

import React, { useState, useEffect } from 'react';
import {
  BookOpen, Plus, X, FileDown, ClipboardList, Trash2,
  Edit2, Share2, Users, Globe, Lock, Calendar, Eye, BarChart3,
  ChevronRight, Search, Filter, MoreVertical
} from 'lucide-react';
import CreatorView from './CreatorView';
import TestCreatorView from './TestCreatorView';
import PerformanceSummaryView from './PerformanceSummaryView';
import TaskPerformanceView from './TaskPerformanceView';
import TaskCreatorManager from './TaskCreatorManager';
import TaskShareModal from './TaskShareModal';
import LeitnerEntity from './LeitnerEntity';
import LeitnerNotesManager from './LeitnerNotesManager';
import { leitnerService } from '../../services/leitnerService';
import { taskService } from '../../services/taskService';
import PdfExportModal from './PdfExportModal';
import PngExportModal from './PngExportModal';
import { useAuth } from '../../contexts/AuthContext';

export const LeitnerManager = ({ bookId, bookData, isOpen, onClose }) => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [tests, setTests] = useState([]);
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [activeTab, setActiveTab] = useState('questions');
  const [loading, setLoading] = useState(true);
  const [showPdfExport, setShowPdfExport] = useState(false);
  const [showPngExport, setShowPngExport] = useState(false);

  // Task management state
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskShare, setShowTaskShare] = useState(false);
  const [taskToShare, setTaskToShare] = useState(null);
  const [taskSearchQuery, setTaskSearchQuery] = useState('');

  const [taskForPerformance, setTaskForPerformance] = useState(null);

  const styling = bookData?.styling || {};

  useEffect(() => {
    if (isOpen && bookId) {
      loadData();
    }
  }, [isOpen, bookId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const questionsData = await leitnerService.getBookQuestions(bookId);
      setQuestions(questionsData);

      const testsData = await leitnerService.getTests(bookId);
      setTests(testsData);

      const notesData = await leitnerService.getLeitnerNotes(bookId);
      setNotes(notesData);

      const tasksData = await taskService.getBookTasks(bookId);
      setTasks(tasksData);

      const resultsData = await leitnerService.getUserTestResults(bookId, user.uid);
      setTestResults(resultsData);
    } catch (error) {
      console.error('Failed to load Leitner data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addQuestion = async (newQuestionData) => {
    try {
      const { stemImage, ...restOfData } = newQuestionData;
      const imageFile = stemImage instanceof File ? stemImage : null;

      const newQuestion = await leitnerService.addQuestion(bookId, restOfData, imageFile, user?.uid);
      setQuestions(prev => [newQuestion, ...prev]);
      return newQuestion;
    } catch (error) {
      console.error('Failed to add question:', error);
      throw error;
    }
  };

  const updateQuestion = async (questionId, updatedQuestionData) => {
    try {
      const originalQuestion = questions.find(q => q.id === questionId);
      const { stemImage, ...restOfData } = updatedQuestionData;

      let imageFile = undefined;

      if (stemImage instanceof File) {
        imageFile = stemImage;
      } else if (stemImage === null) {
        imageFile = null;
      }

      const updated = await leitnerService.updateQuestion(
        bookId,
        questionId,
        restOfData,
        imageFile,
        user?.uid,
        originalQuestion?.stemImage
      );

      setQuestions(prev => prev.map(q => q.id === questionId ? updated : q));
    } catch (error) {
      console.error('Failed to update question:', error);
      throw error;
    }
  };

  const deleteQuestion = async (questionId) => {
    try {
      const questionToDelete = questions.find(q => q.id === questionId);
      await leitnerService.deleteQuestion(
        bookId,
        questionId,
        user?.uid,
        questionToDelete?.stemImage
      );
      setQuestions(prev => prev.filter(q => q.id !== questionId));
    } catch (error) {
      console.error('Failed to delete question:', error);
    }
  };

  const addTest = async (newTest) => {
    try {
      const testWithId = await leitnerService.addTest(bookId, newTest);
      setTests(prev => [...prev, testWithId]);
      setActiveTab('tests');
    } catch (error) {
      console.error('Failed to add test:', error);
    }
  };

  const deleteTest = async (testId) => {
    if (window.confirm('Are you sure you want to delete this test?')) {
      try {
        await leitnerService.deleteTest(bookId, testId);
        setTests(prev => prev.filter(t => t.id !== testId));
      } catch (error) {
        console.error('Failed to delete test:', error);
      }
    }
  };

  // Task handlers
  const addTask = async (taskData) => {
    try {
      const newTask = await taskService.createTask(bookId, taskData, user.uid);
      setTasks(prev => [newTask, ...prev]);
      setActiveTab('tasks');
      setSelectedTask(null);
    } catch (error) {
      console.error('Failed to create task:', error);
      throw error;
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      await taskService.updateTask(bookId, taskId, updates);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updates } : t));
    } catch (error) {
      console.error('Failed to update task:', error);
      throw error;
    }
  };

  const deleteTask = async (taskId) => {
    if (window.confirm('Are you sure you want to delete this task? This cannot be undone.')) {
      try {
        await taskService.deleteTask(bookId, taskId);
        setTasks(prev => prev.filter(t => t.id !== taskId));
      } catch (error) {
        console.error('Failed to delete task:', error);
      }
    }
  };

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'public': return <Globe className="h-4 w-4 text-green-500" />;
      case 'organisation': return <Users className="h-4 w-4 text-blue-500" />;
      default: return <Lock className="h-4 w-4 text-amber-500" />;
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date?.toDate?.() || new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredTasks = tasks.filter(task =>
    task.name?.toLowerCase().includes(taskSearchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-0">
      <div className="bg-white shadow-xl w-full h-screen flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-indigo-600" />
            Leitner Question & Test Manager
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPdfExport(true)}
              disabled={questions.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown className="h-4 w-4" />
              Export PDF
            </button>
            <button
              onClick={() => setShowPngExport(true)}
              disabled={questions.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown className="h-4 w-4" />
              Export PNG
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b bg-gray-50">
          <div className="flex px-6">
            <button
              onClick={() => setActiveTab('questions')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${activeTab === 'questions'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
            >
              Questions ({questions.length})
            </button>
            <button
              onClick={() => setActiveTab('tests')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${activeTab === 'tests'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
            >
              Tests ({tests.length})
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${activeTab === 'notes'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
            >
              Notes ({notes.length})
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${activeTab === 'tasks'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
            >
              Tasks ({tasks.length})
            </button>
            <button
              onClick={() => setActiveTab('performance')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${activeTab === 'performance'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
            >
              Performance
            </button>
            <button
              onClick={() => setActiveTab('entities')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${activeTab === 'entities'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
            >
              Entities
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-gray-400">Loading...</p>
            </div>
          ) : (
            <>
              {/* Questions Tab */}
              {activeTab === 'questions' && (
                <CreatorView
                  bookId={bookId}
                  addQuestion={addQuestion}
                  questions={questions}
                  deleteQuestion={deleteQuestion}
                  updateQuestion={updateQuestion}
                  availableNotes={notes}
                />
              )}

              {activeTab === 'notes' && (
                <LeitnerNotesManager
                  bookId={bookId}
                  notes={notes}
                  onNotesChange={setNotes}
                />
              )}

              {/* Tests Tab */}
              {activeTab === 'tests' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Available Tests</h3>
                    <button
                      onClick={() => setActiveTab('create-test')}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                    >
                      <Plus className="h-4 w-4" />
                      Create New Test
                    </button>
                  </div>

                  {tests.length === 0 ? (
                    <div className="text-center py-16 bg-gray-50 rounded-lg">
                      <p className="text-gray-500 mb-4">No tests created yet.</p>
                      <button
                        onClick={() => setActiveTab('create-test')}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                      >
                        <Plus className="h-4 w-4" />
                        Create Your First Test
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {tests.map(test => (
                        <div key={test.id} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center hover:bg-gray-100 transition">
                          <div>
                            <h4 className="font-semibold text-gray-900">{test.name}</h4>
                            <p className="text-sm text-gray-600">{test.questionIds?.length || 0} questions</p>
                          </div>
                          <button
                            onClick={() => deleteTest(test.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Create Test Tab */}
              {activeTab === 'create-test' && (
                <TestCreatorView
                  questions={questions.filter(q => q.type === 'simple')}
                  addTest={addTest}
                  onCancel={() => setActiveTab('tests')}
                />
              )}

              {/* Tasks Tab */}
              {activeTab === 'tasks' && !selectedTask && !taskForPerformance && (
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Leitner Quiz Tasks</h3>
                    <div className="flex gap-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={taskSearchQuery}
                          onChange={(e) => setTaskSearchQuery(e.target.value)}
                          placeholder="Search tasks..."
                          className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <button
                        onClick={() => setSelectedTask('new')}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                      >
                        <Plus className="h-4 w-4" />
                        Create Task
                      </button>
                    </div>
                  </div>

                  {filteredTasks.length === 0 ? (
                    <div className="text-center py-16 bg-gray-50 rounded-lg">
                      <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">
                        {taskSearchQuery ? 'No tasks match your search' : 'No tasks created yet'}
                      </p>
                      {!taskSearchQuery && (
                        <button
                          onClick={() => setSelectedTask('new')}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        >
                          <Plus className="h-4 w-4" />
                          Create Your First Task
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredTasks.map(task => (
                        <div
                          key={task.id}
                          className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition border border-gray-200"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-gray-900 truncate">{task.name}</h4>
                                {getVisibilityIcon(task.visibility)}
                              </div>

                              {task.description && (
                                <p className="text-sm text-gray-600 line-clamp-1 mb-2">
                                  {task.description}
                                </p>
                              )}

                              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <ClipboardList className="h-3.5 w-3.5" />
                                  {task.questionIds?.length || 0} questions
                                </span>
                                {task.taskPdfUrl && (
                                  <span className="flex items-center gap-1 text-emerald-700">
                                    <FileDown className="h-3.5 w-3.5" />
                                    PDF attached
                                  </span>
                                )}

                                {task.assignedUsers?.length > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Users className="h-3.5 w-3.5" />
                                    {task.assignedUsers.length} assigned
                                  </span>
                                )}

                                {task.isTimeBound && (
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3.5 w-3.5" />
                                    {formatDate(task.startDate)} - {formatDate(task.endDate)}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setTaskForPerformance(task)}
                                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                title="View performance"
                              >
                                <BarChart3 className="h-4 w-4" />
                              </button>
                              {task.taskPdfUrl && (
                                <button
                                  onClick={() => window.open(task.taskPdfUrl, '_blank')}
                                  className="p-2 text-emerald-700 hover:bg-emerald-50 rounded-lg transition"
                                  title={task.taskPdfFilename ? `Download ${task.taskPdfFilename}` : 'Download task PDF'}
                                >
                                  <FileDown className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => setSelectedTask(task)}
                                className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition"
                                title="Edit task"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setTaskToShare(task);
                                  setShowTaskShare(true);
                                }}
                                className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition"
                                title="Share task"
                              >
                                <Share2 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => window.open(`/tasks/${task.id}`, '_blank')}
                                className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition"
                                title="Preview task"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => deleteTask(task.id)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                                title="Delete task"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'tasks' && taskForPerformance && (
                <TaskPerformanceView
                  key={taskForPerformance.id}
                  bookId={bookId}
                  task={taskForPerformance}
                  questions={(() => {
                    const taskQIds = new Set(taskForPerformance.questionIds || []);
                    const result = [];

                    questions.forEach(q => {
                      if (q.type === 'stem' && q.subQuestions?.length > 0) {
                        // Check if parent ID is in task OR any subQuestion ID is in task
                        const parentInTask = taskQIds.has(q.id);
                        const matchedSubs = q.subQuestions.filter(sq => taskQIds.has(sq.id));

                        if (parentInTask || matchedSubs.length > 0) {
                          // Include subQuestions (all if parent matched, or just matched ones)
                          const subsToInclude = parentInTask ? q.subQuestions : matchedSubs;
                          subsToInclude.forEach(sq => {
                            result.push({
                              ...sq,
                              parentId: q.id,
                              parentStemText: q.stemText,
                              parentStemImage: q.stemImage
                            });
                          });
                        }
                      } else if (taskQIds.has(q.id)) {
                        result.push(q);
                      }
                    });

                    return result;
                  })()}
                  styling={styling}
                  viewMode="owner"
                  onBack={() => setTaskForPerformance(null)}
                />
              )}

              {/* Create/Edit Task */}
              {activeTab === 'tasks' && selectedTask && (
                <TaskCreatorManager
                  bookId={bookId}
                  questions={questions}
                  bookData={bookData}
                  styling={styling}
                  task={selectedTask === 'new' ? null : selectedTask}
                  onSave={selectedTask === 'new' ? addTask : (data) => updateTask(selectedTask.id, data)}
                  onTaskPdfUpdated={(updates) => {
                    if (!selectedTask || selectedTask === 'new') return;
                    setTasks(prev => prev.map(t => (
                      t.id === selectedTask.id ? { ...t, ...updates } : t
                    )));
                    setSelectedTask(prev => (prev && prev !== 'new' ? { ...prev, ...updates } : prev));
                  }}
                  onCancel={() => setSelectedTask(null)}
                />
              )}

              {/* Performance Tab */}
              {activeTab === 'performance' && (
                <PerformanceSummaryView
                  bookId={bookId}
                  tests={tests}
                  results={testResults}
                />
              )}

              {activeTab === 'entities' && (
                <LeitnerEntity
                  bookId={bookId}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* PDF Export Modal */}
      <PdfExportModal
        isOpen={showPdfExport}
        onClose={() => setShowPdfExport(false)}
        questions={questions}
        title="Question Bank"
        bookData={bookData}
      />

      {/* PNG Export Modal */}
      <PngExportModal
        isOpen={showPngExport}
        onClose={() => setShowPngExport(false)}
        questions={questions}
        bookData={bookData}
      />

      {/* Task Share Modal */}
      {showTaskShare && taskToShare && (
        <TaskShareModal
          task={taskToShare}
          bookId={bookId}
          bookData={bookData}
          onClose={() => {
            setShowTaskShare(false);
            setTaskToShare(null);
          }}
          onUpdate={(updates) => {
            setTasks(prev => prev.map(t =>
              t.id === taskToShare.id ? { ...t, ...updates } : t
            ));
          }}
        />
      )}
    </div>
  );
};

export default LeitnerManager;
